import {PageTitle} from '_metronic/layout/core'
import {FC, useState, useEffect} from 'react'

import './style.css'

import Step1 from './Step1'
import Step2 from './Step2'
import Step3 from './Step3'
import Step4 from './Step4'
import handleApiError from 'thesis-and-dissertations/helpers/handleApiError'
import Api from '../../../thesis-and-dissertations/helpers/api'
import {toast} from 'react-toastify'
import {useNavigate} from 'react-router-dom'

const api = new Api()

type Props = {
  handleToPage: (page: string) => void
}

const RegisterPage: FC<Props> = ({handleToPage}) => {
  const navigate = useNavigate()

  const [step, setStep] = useState<any>(localStorage.getItem('currentStep') ?? 3) // Start from step 3 (Essay)
  const [showModalConfirm, setShowModalConfirm] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [stepData, setStepData] = useState<any>({})
  const [errors, setErrors] = useState<any>({})
  
  // State untuk field_steps configuration dari API
  const [fieldSteps, setFieldSteps] = useState<any>(null)
  const [fieldStepsLoading, setFieldStepsLoading] = useState<boolean>(true)

  useEffect(() => {
    fetchFieldSteps()
  }, [])

  useEffect(() => {
    if (stepData?.step == 5 && stepData?.is_selesai == 1) {
      registerPana()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepData])

  // Check if current step is disabled after fieldSteps loaded, skip to next enabled step
  useEffect(() => {
    if (fieldSteps && isStepDisabled(parseInt(step))) {
      const nextEnabledStep = getNextEnabledStep(parseInt(step) - 1)
      setStep(nextEnabledStep)
      localStorage.setItem('currentStep', nextEnabledStep.toLocaleString())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldSteps])

  // Fetch field_steps configuration from API
  const fetchFieldSteps = () => {
    setFieldStepsLoading(true)
    api
      .getRegisterPana()
      .then((response: any) => {
        const {data} = response.data
        if (data?.field_steps) {
          setFieldSteps(data.field_steps)
        }
        setFieldStepsLoading(false)
      })
      .catch((error: any) => {
        // Jika error, fieldSteps tetap null (gunakan default)
        setFieldStepsLoading(false)
      })
  }

  // Get field configuration for a specific step
  const getStepFieldConfig = (stepNum: number) => {
    if (!fieldSteps) return null
    return fieldSteps[`step_${stepNum}`]?.field || null
  }

  // Check if a step is disabled
  const isStepDisabled = (stepNum: number) => {
    if (!fieldSteps) return false
    return fieldSteps[`step_${stepNum}`]?.disable == true
  }

  // Get next enabled step (skip disabled steps, limited to 3-5 range)
  const getNextEnabledStep = (currentStep: number): number => {
    let nextStep = currentStep + 1
    while (nextStep <= 5 && isStepDisabled(nextStep)) {
      nextStep++
    }
    return nextStep > 5 ? 5 : nextStep
  }

  // Get previous enabled step (skip disabled steps, limited to 3-5 range)
  const getPreviousEnabledStep = (currentStep: number): number => {
    let prevStep = currentStep - 1
    while (prevStep >= 3 && isStepDisabled(prevStep)) {
      prevStep--
    }
    return prevStep < 3 ? 3 : prevStep
  }

  // Get first enabled step (now step 3 is the first)
  const getFirstEnabledStep = (): number => {
    for (let i = 3; i <= 5; i++) {
      if (!isStepDisabled(i)) return i
    }
    return 3
  }

  // Get list of enabled steps for progress bar (only 3, 4, 5)
  const getEnabledSteps = (): number[] => {
    const enabled: number[] = []
    for (let i = 3; i <= 5; i++) {
      if (!isStepDisabled(i)) enabled.push(i)
    }
    return enabled.length > 0 ? enabled : [3, 4, 5]
  }

  // Get last enabled step
  const getLastEnabledStep = (): number => {
    const enabled = getEnabledSteps()
    return enabled[enabled.length - 1]
  }

  // Check if current step is the last enabled step
  const isLastEnabledStep = (currentStep: number): boolean => {
    return currentStep >= getLastEnabledStep()
  }

  // Helper function to get plain text length from HTML string
  const getPlainTextLength = (htmlString: any) => {
    if (typeof htmlString !== 'string') return 0
    const div = document.createElement('div')
    div.innerHTML = htmlString
    const text = div.textContent || div.innerText || ''
    return text.trim().length
  }

  const handleNext = () => {
    const currentStepNum = parseInt(step)
    
    // Validasi khusus untuk step 3 (Ringkasan Essay)
    if (currentStepNum == 3) {
      const essayLength = getPlainTextLength(stepData?.ringkasan_proposal)
      
      if (essayLength < 1000) {
        toast.error('Ringkasan essay minimal 1000 karakter. Saat ini: ' + essayLength + ' karakter')
        setErrors({ringkasan_proposal: 'Ringkasan essay minimal 1000 karakter'})
        return
      }
      
      if (essayLength > 3000) {
        toast.error('Ringkasan essay maksimal 3000 karakter. Saat ini: ' + essayLength + ' karakter')
        setErrors({ringkasan_proposal: 'Ringkasan essay maksimal 3000 karakter'})
        return
      }
    }
    
    // Validasi khusus untuk step 4 (Surat Pernyataan)
    if (currentStepNum == 4) {
      if (!stepData?.is_file_uploaded) {
        toast.error('Anda harus mengupload surat pernyataan bermaterai sebelum melanjutkan')
        setErrors({is_file_upload: 'Silakan upload surat pernyataan bermaterai'})
        return
      }
    }
    
    // Jika sudah di step terakhir yang enabled, tampilkan modal konfirmasi
    if (isLastEnabledStep(currentStepNum)) {
      handleShowModal(true)
      return
    }

    registerPana()
  }

  const handlePrevious = () => {
    const currentStepNum = parseInt(step)
    const prevStep = getPreviousEnabledStep(currentStepNum)
    
    // Jika sudah di step pertama yang enabled, kembali ke halaman sebelumnya
    if (currentStepNum <= getFirstEnabledStep()) {
      handleToPage('notRegistered')
      return
    }

    setStep(prevStep)
    localStorage.setItem('currentStep', prevStep.toLocaleString())
  }

  const handleShowModal = (action: boolean) => {
    setShowModalConfirm(action)
  }

  const handleStepData = (data: any) => {
    setErrors({})
    setStepData(data)
  }

  const registerPana = () => {
    setLoading(true)
    api
      .registerPana(stepData)
      .then((response: any) => {
        setLoading(false)
        const {data} = response.data

        const currentStepNum = parseInt(step)
        const lastStep = getLastEnabledStep()

        // Jika sudah di step terakhir dan selesai, redirect ke beranda
        if (currentStepNum >= lastStep || stepData?.is_selesai == 1) {
          navigate('/pana-beranda')
          localStorage.removeItem('currentStep')
          return
        }

        // Get next enabled step (skip disabled steps)
        let nextStep = getNextEnabledStep(currentStepNum)

        setStep(nextStep)
        localStorage.setItem('currentStep', nextStep.toLocaleString())
      })
      .catch((error: any) => {
        setLoading(false)
        const {data, status} = error.response

        handleApiError(error, navigate)
        toast.error(data?.meta?.message)
        setErrors(data?.meta?.error)
      })
  }

  const stepPage = (page: number, showModal: boolean) => {
    const enabledSteps = getEnabledSteps()
    switch (page) {
      case 3:
        return (
          <Step3
            step={step}
            showModal={showModal}
            handleShowModalAlert={handleShowModal}
            handleStepData={handleStepData}
            errorProps={errors}
            asTeam={false} // Always false, only individual mode
            fieldConfig={getStepFieldConfig(3)}
            enabledSteps={enabledSteps}
            currentStep={page}
          />
        )
      case 4:
        return <Step4 handleStepData={handleStepData} errorProps={errors} asTeam={false} enabledSteps={enabledSteps} currentStep={page} />
      case 5:
        return (
          <Step3
            step={step}
            showModal={showModal}
            handleShowModalAlert={handleShowModal}
            handleStepData={handleStepData}
            errorProps={errors}
            asTeam={false} // Always false, only individual mode
            fieldConfig={getStepFieldConfig(3)}
            enabledSteps={enabledSteps}
            currentStep={page}
          />
        )
    }
  }

  return (
    <>
      <div className='col-md-12'>
        <div className='card'>
          <div className='px-10 py-5'>
            <p className='h4 pt-3'>DAFTAR PANA</p>
          </div>

          <span className='border-top' />

          {fieldStepsLoading ? (
            <div className='d-flex justify-content-center align-items-center py-20'>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          ) : (
            stepPage(parseInt(step), showModalConfirm)
          )}

          {!fieldStepsLoading && (
            <>
              <span className='border-top mt-15' />

              <div className='d-flex justify-content-end p-5'>
                <button
                  className='btn btn-secondary btn-sm mx-3'
                  onClick={() => {
                    parseInt(step) > getFirstEnabledStep() ? handlePrevious() : navigate('/pana-beranda')
                  }}
                  disabled={loading}
                >
                  Sebelumnya
                </button>

                <button className='btn btn-primary btn-sm' onClick={handleNext} disabled={loading}>
                  {loading ? 'Loading...' : isLastEnabledStep(parseInt(step)) ? 'Submit' : 'Selanjutnya'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

const Register: FC<Props> = ({handleToPage}) => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Dashboard',
            path: '/pana-beranda',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '/',
            path: '',
            isSeparator: false,
            isActive: false,
          },
        ]}
      >
        Form Pendaftaran PANA
      </PageTitle>
      <RegisterPage handleToPage={handleToPage} />
    </>
  )
}

export default Register
